import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Tags, ChevronLeft, Search, Loader2, RotateCcw, Check, TrendingUp, TrendingDown,
  ShieldCheck, Sparkles, Wallet, Info, Building2, Database, Plus, Trash2, Save, X, ListPlus,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const PETROL = "#0c2b38";
const NEON = "#22c55e";
const money = (n) => `S/ ${Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const n2 = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const GRUPOS = ["TODOS", "MANO DE OBRA", "MATERIALES", "EQUIPOS", "SUBCONTRATOS"];

/* Smart Input de precio: select-all al enfocar, Enter/blur confirma, 2 decimales. */
const PrecioInput = ({ cod, value, base, onCommit, busy }) => {
  const [v, setV] = useState(String(value ?? ""));
  const [editing, setEditing] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (!editing) setV(String(value ?? "")); }, [value, editing]);
  const commit = () => {
    setEditing(false);
    const num = parseFloat(v);
    if (isNaN(num) || num < 0) { setV(String(value ?? "")); return; }
    if (Math.abs(num - Number(value)) < 0.0001) return;
    onCommit(num);
  };
  const changed = Math.abs(Number(value) - Number(base)) > 0.0001;
  return (
    <span className={`flex items-center rounded-md border px-1.5 py-0.5 bg-white transition-colors ${changed ? "border-emerald-300 ring-1 ring-emerald-100" : "border-slate-200"} focus-within:border-emerald-500`}>
      <span className="text-[9px] font-bold text-slate-400 mr-0.5">S/</span>
      <input ref={ref} data-testid={`vm-precio-input-${cod}`} type="number" min="0" step="0.01" value={v} disabled={busy}
        onFocus={(e) => { setEditing(true); e.target.select(); }}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
        onBlur={commit}
        className="w-20 text-right text-[12px] font-bold text-slate-800 focus:outline-none bg-transparent disabled:opacity-50" />
      {busy && <Loader2 size={11} className="animate-spin text-emerald-500 ml-1" />}
    </span>
  );
};

const CAPS = [
  [1, "01 · Obras provisionales"], [2, "02 · Estructuras"], [3, "03 · Arquitectura"],
  [4, "04 · Inst. sanitarias"], [5, "05 · Inst. eléctricas"], [6, "06 · Inst. gas/mecánicas"], [7, "07 · Varios / otros"],
];
const EMPTY = { capitulo: 3, descripcion: "", unidad: "und", metrado: "", pu: "" };

/* Fase B · Gestión de Partidas Personalizadas (aditivas, aisladas por usuario). */
const MisPartidas = ({ onChanged }) => {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vm/estructural/partidas-custom");
      setItems(data.partidas || []); setTotal(data.total || 0);
    } catch (e) { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setForm(EMPTY); setEditId(null); };
  const refresh = async () => { await load(); onChanged && onChanged(); window.dispatchEvent(new Event("corex-refresh")); };

  const submit = async () => {
    const metrado = parseFloat(form.metrado), pu = parseFloat(form.pu);
    if (!form.descripcion.trim()) return toast.error("Ingresa la descripción de la partida");
    if (isNaN(metrado) || isNaN(pu) || metrado < 0 || pu < 0) return toast.error("Metrado y P.U. deben ser números ≥ 0");
    setBusy(true);
    const body = { capitulo: Number(form.capitulo), descripcion: form.descripcion.trim(), unidad: form.unidad || "und", metrado, pu };
    try {
      if (editId) { await api.put(`/vm/estructural/partidas-custom/${editId}`, body); toast.success("Partida actualizada"); }
      else { await api.post("/vm/estructural/partidas-custom", body); toast.success("Partida añadida al presupuesto"); }
      reset(); await refresh();
    } catch (e) { toast.error("No se pudo guardar la partida"); } finally { setBusy(false); }
  };
  const edit = (p) => { setEditId(p.id); setForm({ capitulo: p.capitulo, descripcion: p.descripcion, unidad: p.unidad, metrado: String(p.metrado), pu: String(p.pu) }); };
  const del = async (p) => { try { await api.delete(`/vm/estructural/partidas-custom/${p.id}`); toast.message("Partida eliminada", { description: p.descripcion }); if (editId === p.id) reset(); await refresh(); } catch (e) { toast.error("No se pudo eliminar"); } };

  const inputCls = "rounded-md border border-slate-200 px-2 py-1.5 text-[12px] text-slate-700 focus:outline-none focus:border-emerald-500 bg-white";
  return (
    <div className="space-y-3" data-testid="vm-partidas-panel">
      {/* Formulario adicionar/editar */}
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
        <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-emerald-700 mb-2">
          {editId ? <Save size={13} /> : <Plus size={13} />} {editId ? "Editar partida" : "Adicionar partida personalizada"}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_2.5fr_0.8fr_0.9fr_1fr_auto] gap-2 items-center">
          <select value={form.capitulo} onChange={(e) => setForm({ ...form, capitulo: e.target.value })} data-testid="vm-partida-capitulo" className={inputCls}>
            {CAPS.map(([n, l]) => <option key={n} value={n}>{l}</option>)}
          </select>
          <input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="Descripción de la partida" data-testid="vm-partida-desc" className={inputCls} />
          <input value={form.unidad} onChange={(e) => setForm({ ...form, unidad: e.target.value })} placeholder="und" data-testid="vm-partida-unidad" className={inputCls} />
          <input type="number" min="0" step="0.01" value={form.metrado} onChange={(e) => setForm({ ...form, metrado: e.target.value })} placeholder="Metrado" data-testid="vm-partida-metrado" className={inputCls + " text-right"} />
          <input type="number" min="0" step="0.01" value={form.pu} onChange={(e) => setForm({ ...form, pu: e.target.value })} placeholder="P.U. S/" data-testid="vm-partida-pu" className={inputCls + " text-right"} />
          <div className="flex items-center gap-1">
            <button onClick={submit} disabled={busy} data-testid="vm-partida-guardar" className="flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : (editId ? <Save size={13} /> : <Plus size={13} />)} {editId ? "Guardar" : "Añadir"}
            </button>
            {editId && <button onClick={reset} data-testid="vm-partida-cancelar" className="rounded-md border border-slate-200 px-2 py-1.5 text-slate-500 hover:bg-white"><X size={13} /></button>}
          </div>
        </div>
        <div className="mt-1.5 text-[10px] text-slate-400">Subtotal partida = Metrado × P.U. Se suma al Costo Directo de tu proyecto (aislado; no afecta la plantilla maestra Santisteban).</div>
      </div>

      {/* Lista de partidas */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-[70px_1fr_60px_90px_110px_120px_60px] gap-2 bg-slate-800 px-3 py-2 text-[9px] font-bold uppercase tracking-wide text-slate-200">
          <span>Cap.</span><span>Descripción</span><span>Und</span><span className="text-right">Metrado</span><span className="text-right">P.U. (S/)</span><span className="text-right">Subtotal</span><span className="text-center">Acc.</span>
        </div>
        {items === null ? (
          <div className="grid place-items-center py-12"><Loader2 size={22} className="animate-spin text-slate-300" /></div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-[12px] text-slate-400">Aún no has añadido partidas personalizadas. Usa el formulario de arriba.</div>
        ) : (
          <div className="divide-y divide-slate-50">
            {items.map((p) => (
              <div key={p.id} className="grid grid-cols-[70px_1fr_60px_90px_110px_120px_60px] gap-2 items-center px-3 py-1.5 text-[12px] hover:bg-slate-50" data-testid={`vm-partida-row-${p.id}`}>
                <span className="font-mono text-[10px] text-slate-400">{String(p.capitulo).padStart(2, "0")}</span>
                <span className="font-semibold text-slate-700 truncate" title={p.descripcion}>{p.descripcion}</span>
                <span className="text-[10px] text-slate-400">{p.unidad}</span>
                <span className="text-right font-mono text-[11px] text-slate-600">{n2(p.metrado)}</span>
                <span className="text-right font-mono text-[11px] text-slate-600">{n2(p.pu)}</span>
                <span className="text-right font-mono text-[11px] font-extrabold text-emerald-700" data-testid={`vm-partida-subtotal-${p.id}`}>{money(p.subtotal)}</span>
                <span className="flex items-center justify-center gap-1.5">
                  <button onClick={() => edit(p)} data-testid={`vm-partida-editar-${p.id}`} className="text-slate-400 hover:text-emerald-600"><Save size={13} /></button>
                  <button onClick={() => del(p)} data-testid={`vm-partida-eliminar-${p.id}`} className="text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-t border-slate-200">
              <span className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500">Total partidas personalizadas</span>
              <span className="font-mono text-[13px] font-black text-emerald-700" data-testid="vm-partidas-total">{money(total)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function VmPrecios() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const from = new URLSearchParams(useLocation().search).get("from");
  const back = from === "estructural"
    ? { route: "/software/estructural", label: "Estructuras", icon: Database }
    : { route: "/software/vivienda-multifamiliar", label: "Inicio", icon: ChevronLeft };
  const [rows, setRows] = useState(null);
  const [esAdmin, setEsAdmin] = useState(false);
  const [q, setQ] = useState("");
  const [grupo, setGrupo] = useState("TODOS");
  const [cd, setCd] = useState(null);
  const [busyCod, setBusyCod] = useState(null);
  const [impacto, setImpacto] = useState(null);
  const [tab, setTab] = useState("insumos");
  const [version, setVersion] = useState(1);
  const [pendientes, setPendientes] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const [versiones, setVersiones] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const fetchCatalog = useCallback(async () => {
    try {
      const { data } = await api.get("/vm/estructural/precios/insumos");
      setRows(data.insumos || []);
      setEsAdmin(!!data.esAdmin);
      setVersion(data.version || 1);
      setPendientes(data.pendientes || 0);
    } catch (e) { toast.error("No se pudo cargar el catálogo de insumos"); setRows([]); }
  }, []);

  const fetchCd = useCallback(async () => {
    try {
      const { data } = await api.get("/vm/estructural/param-cd");
      if (data?.disponible) setCd(data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { fetchCatalog(); fetchCd(); }, [fetchCatalog, fetchCd]);

  const commitPrecio = async (row, precio) => {
    setBusyCod(row.cod);
    try {
      const { data } = await api.put("/vm/estructural/precios/draft", { cod: row.cod, precio });
      const delta = data?.impacto?.deltaCD;
      const label = `Borrador (${row.desc}): ${delta >= 0 ? "+" : "−"}${money(Math.abs(delta || 0))}`;
      setImpacto({ label, delta, cod: row.cod, desc: row.desc, cdDespues: data?.impacto?.cdBorrador });
      toast.message(`Precio en borrador · ${row.desc}`, { description: `Impacto previsto en CD: ${delta >= 0 ? "+" : "−"}${money(Math.abs(delta || 0))}. Presiona ACTUALIZAR PRECIOS para confirmar.` });
      await fetchCatalog();
    } catch (e) {
      toast.error("No se pudo guardar el borrador del precio");
    } finally { setBusyCod(null); }
  };

  const publishPrices = async () => {
    if (!pendientes) { toast.message("No hay cambios en borrador para publicar."); return; }
    const vn = version + 1;
    const msg = esAdmin
      ? `Se actualizarán ${pendientes} insumo(s) y se generará la Versión V${vn}. ¿Continuar?`
      : `Se actualizarán ${pendientes} insumo(s) en tu proyecto. ¿Continuar?`;
    if (!window.confirm(msg)) return;
    setPublishing(true);
    try {
      const { data } = await api.post("/vm/estructural/precios/publish");
      window.dispatchEvent(new Event("corex-refresh"));
      toast.success(esAdmin ? `Versión V${data.version} publicada · ${data.actualizados} insumo(s)` : `${data.actualizados} precio(s) actualizado(s) en tu proyecto`);
      setImpacto(null);
      await Promise.all([fetchCatalog(), fetchCd()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar los precios");
    } finally { setPublishing(false); }
  };

  const restorePrices = async () => {
    // F2 · RESTAURAR PRECIOS. Admin → selector de versiones publicadas (carga como borrador).
    // Beneficiario → descarta el borrador vigente.
    if (esAdmin) {
      setShowRestore(true);
      setVersiones(null);
      try {
        const { data } = await api.get("/vm/estructural/precios/versiones");
        setVersiones(data.versiones || []);
      } catch (e) { toast.error("No se pudieron cargar las versiones"); setVersiones([]); }
      return;
    }
    if (!pendientes) { toast.message("No hay borrador que restaurar."); return; }
    if (!window.confirm(`Se descartará el borrador y se eliminarán tus precios de ESTE proyecto, volviendo a la Versión maestra vigente V${version}. No afecta tus otros proyectos. ¿Continuar?`)) return;
    try {
      const { data } = await api.post("/vm/estructural/precios/restore-mine");
      setImpacto(null);
      toast.success(`Precios restaurados a la maestra V${version}${data?.overridesEliminados ? ` · ${data.overridesEliminados} override(s) eliminados` : ""}`);
      await Promise.all([fetchCatalog(), fetchCd()]);
    } catch (e) { toast.error("No se pudo restaurar"); }
  };

  const restoreVersion = async (v) => {
    setRestoring(true);
    try {
      const { data } = await api.post(`/vm/estructural/precios/restore/${v.version}`);
      setShowRestore(false);
      setImpacto(null);
      if (data.cargadosEnBorrador > 0) {
        toast.success(`${v.label} cargada en borrador · ${data.cargadosEnBorrador} insumo(s). Presiona ACTUALIZAR PRECIOS para publicarla.`);
      } else {
        toast.message(`${v.label} es idéntica a la versión vigente · sin cambios que cargar.`);
      }
      await fetchCatalog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo restaurar la versión");
    } finally { setRestoring(false); }
  };

  const revert = async (row) => {
    setBusyCod(row.cod);
    try {
      await api.delete(`/vm/estructural/precios/draft/${row.cod}`);
      toast.message(`Borrador descartado · ${row.desc}`, { description: money(row.precioVigente ?? row.precioBase) });
      await fetchCatalog();
    } catch (e) { toast.error("No se pudo descartar"); } finally { setBusyCod(null); }
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    const s = q.trim().toLowerCase();
    return rows.filter((r) =>
      (grupo === "TODOS" || r.grupo === grupo) &&
      (!s || r.desc.toLowerCase().includes(s) || String(r.cod).includes(s))
    );
  }, [rows, q, grupo]);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="vm-precios-page">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <button onClick={() => navigate(back.route)} data-testid="vm-precios-back"
            className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] font-bold text-slate-600 hover:bg-slate-50">
            <ChevronLeft size={15} /> {back.label}
          </button>
          <div className="grid h-9 w-9 place-items-center rounded-lg text-white" style={{ background: PETROL }}><Tags size={18} /></div>
          <div className="leading-tight">
            <div className="text-[15px] font-extrabold text-slate-800">Actualización de Precios</div>
            <div className="text-[11px] text-slate-500">Estación de Inteligencia de Costos · cascada en tiempo real</div>
          </div>
          <span className="ml-2 flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-extrabold"
            style={esAdmin ? { background: "rgba(37,99,235,0.12)", color: "#1d4ed8" } : { background: NEON + "22", color: "#15803d" }}
            data-testid="vm-precios-rol">
            {esAdmin ? <><ShieldCheck size={12} /> Plantilla Maestra (Admin)</> : <><Building2 size={12} /> Mi Proyecto</>}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden sm:flex flex-col items-end leading-tight mr-1" data-testid="vm-precios-version-info">
              <span className="text-[11px] font-extrabold text-slate-700">Versión vigente: V{version}</span>
              <span className={`text-[10px] font-bold ${pendientes ? "text-amber-600" : "text-slate-400"}`}>Cambios pendientes: {pendientes} insumo{pendientes === 1 ? "" : "s"}</span>
            </div>
            <button onClick={restorePrices} disabled={publishing || restoring} data-testid="vm-precios-restaurar"
              className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-[11px] font-extrabold text-slate-600 hover:bg-slate-50 disabled:opacity-40">
              <RotateCcw size={13} /> RESTAURAR PRECIOS
            </button>
            <button onClick={publishPrices} disabled={!pendientes || publishing} data-testid="vm-precios-actualizar"
              className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-[11px] font-extrabold text-white shadow-lifted disabled:opacity-40"
              style={{ background: pendientes ? PETROL : "#94a3b8" }}>
              {publishing ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} ACTUALIZAR PRECIOS{pendientes ? ` (${pendientes})` : ""}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-4 space-y-4">
        {/* Banda de Costo Directo en vivo + impacto */}
        <div className="grid gap-3 sm:grid-cols-[1fr_1.4fr]">
          <div className="rounded-xl p-3 text-white shadow-lifted" style={{ background: PETROL }} data-testid="vm-precios-cd">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-300"><Wallet size={12} /> Costo Directo del proyecto</div>
            <div className="mt-1 text-2xl font-extrabold" style={{ color: NEON }} data-testid="vm-precios-cd-valor">
              {cd ? money(cd.costoDirectoConAjuste ?? cd.costoDirecto) : "S/ —"}
            </div>
            <div className="mt-0.5 text-[10px] text-slate-400">
              {cd ? `${cd.modo === "ESPECIFICO" ? `Nivel ${cd.pisos}` : (cd.pisos > 1 ? `Acumulado · ${cd.pisos} pisos` : "1 piso + parapeto")} · reacciona al instante` : "Cargando motor…"}
            </div>
          </div>
          <div className={`rounded-xl border p-3 ${impacto ? (impacto.delta >= 0 ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50") : "border-slate-200 bg-white"}`} data-testid="vm-precios-impacto">
            <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wide text-slate-500"><Sparkles size={12} /> Impacto del último cambio de precio</div>
            {impacto ? (
              <div className="mt-1.5 flex items-center gap-2">
                {impacto.delta >= 0 ? <TrendingUp size={20} className="text-amber-600" /> : <TrendingDown size={20} className="text-emerald-600" />}
                <div className="leading-tight">
                  <div className="text-[13px] font-extrabold" style={{ color: impacto.delta >= 0 ? "#b45309" : "#15803d" }} data-testid="vm-precios-impacto-valor">
                    {impacto.delta >= 0 ? "+" : "−"}{money(Math.abs(impacto.delta || 0))}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate max-w-[340px]">{impacto.desc}</div>
                </div>
              </div>
            ) : (
              <div className="mt-1.5 text-[11px] text-slate-400">Edita un precio y presiona Enter para ver el efecto cascada sobre el edificio.</div>
            )}
          </div>
        </div>

        {/* Pestañas: Insumos · Mis Partidas */}
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5 w-fit">
          <button onClick={() => setTab("insumos")} data-testid="vm-tab-insumos"
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition-colors ${tab === "insumos" ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            <Tags size={13} /> Insumos (Precios)
          </button>
          <button onClick={() => setTab("partidas")} data-testid="vm-tab-partidas"
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition-colors ${tab === "partidas" ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            <ListPlus size={13} /> Mis Partidas
          </button>
        </div>

        {tab === "partidas" ? (
          <MisPartidas onChanged={fetchCd} />
        ) : (
        <>
        {/* Controles */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 flex-1 min-w-[200px]">
            <Search size={14} className="text-slate-400" />
            <input data-testid="vm-precios-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar insumo por nombre o código…"
              className="flex-1 text-[12px] text-slate-700 focus:outline-none bg-transparent" />
          </span>
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
            {GRUPOS.map((g) => (
              <button key={g} onClick={() => setGrupo(g)} data-testid={`vm-precios-grupo-${g.replace(/\s+/g, "-").toLowerCase()}`}
                className={`rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${grupo === g ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                {g === "MANO DE OBRA" ? "M. OBRA" : g}
              </button>
            ))}
          </div>
        </div>

        {/* Tabla */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="grid grid-cols-[70px_1fr_60px_110px_140px_90px] gap-2 bg-slate-800 px-3 py-2 text-[9px] font-bold uppercase tracking-wide text-slate-200">
            <span>Código</span><span>Descripción</span><span>Und</span><span className="text-right">Precio base</span><span className="text-right">Precio actual</span><span className="text-center">Estado</span>
          </div>
          {rows === null ? (
            <div className="grid place-items-center py-16"><Loader2 size={24} className="animate-spin text-slate-300" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-[12px] text-slate-400">Sin insumos que coincidan con el filtro.</div>
          ) : (
            <div className="max-h-[calc(100vh-320px)] overflow-y-auto divide-y divide-slate-50">
              {filtered.map((r) => (
                <div key={r.cod} className={`grid grid-cols-[70px_1fr_60px_110px_140px_90px] gap-2 items-center px-3 py-1.5 text-[12px] hover:bg-slate-50 ${r.pendiente ? "bg-amber-50/60" : (r.personalizado ? "bg-emerald-50/40" : "")}`} data-testid={`vm-precio-row-${r.cod}`}>
                  <span className="font-mono text-[10px] text-slate-400">{r.cod}</span>
                  <span className="font-semibold text-slate-700 leading-tight truncate" title={r.desc}>{r.desc}</span>
                  <span className="text-[10px] text-slate-400">{r.unidad}</span>
                  <span className={`text-right font-mono text-[11px] ${(r.pendiente || r.personalizado) ? "text-slate-400 line-through" : "text-slate-600"}`}>{n2(r.precioBase)}</span>
                  <span className="flex justify-end"><PrecioInput cod={r.cod} value={r.precioActual} base={r.precioBase} busy={busyCod === r.cod} onCommit={(p) => commitPrecio(r, p)} /></span>
                  <span className="flex items-center justify-center gap-1">
                    {r.pendiente ? (
                      <>
                        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[8px] font-extrabold text-amber-700" title="Cambio en borrador (sin publicar)">BORRADOR</span>
                        <button onClick={() => revert(r)} data-testid={`vm-precio-revert-${r.cod}`} title="Descartar borrador" className="text-slate-400 hover:text-red-500"><X size={13} /></button>
                      </>
                    ) : r.personalizado ? (
                      <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[8px] font-extrabold text-emerald-700" title={esAdmin ? "Precio maestro vigente" : "Precio personalizado (tu proyecto)"}>{esAdmin ? "MAESTRO" : "PERSONAL"}</span>
                    ) : (
                      <Check size={13} className="text-slate-200" />
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-start gap-1.5 text-[10px] text-slate-400">
          <Info size={12} className="shrink-0 mt-0.5" />
          {esAdmin
            ? "Como Administrador, tus cambios actualizan la Plantilla Maestra (precios base para todos los usuarios). El motor recalcula APU, partidas y capítulos al instante."
            : "Tus cambios se guardan solo en tu proyecto (Precio Personalizado) sin afectar la plantilla maestra. El motor recalcula el edificio completo al instante."}
        </div>
        </>
        )}
      </main>

      {/* F2 · Selector de versiones publicadas (RESTAURAR PRECIOS · admin) */}
      {showRestore && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="vm-precios-restore-modal">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 text-white" style={{ background: PETROL }}>
              <RotateCcw size={16} />
              <div className="leading-tight">
                <div className="text-[13px] font-extrabold">Restaurar versión de precios</div>
                <div className="text-[10px] text-slate-300">Se carga como borrador · publica con ACTUALIZAR PRECIOS</div>
              </div>
              <button onClick={() => setShowRestore(false)} data-testid="vm-precios-restore-close" className="ml-auto text-slate-300 hover:text-white"><X size={16} /></button>
            </div>
            <div className="max-h-[50vh] overflow-y-auto divide-y divide-slate-100">
              {versiones === null ? (
                <div className="grid place-items-center py-12"><Loader2 size={22} className="animate-spin text-slate-300" /></div>
              ) : versiones.length === 0 ? (
                <div className="py-10 text-center text-[12px] text-slate-400">No hay versiones publicadas.</div>
              ) : (
                versiones.slice().reverse().map((v) => (
                  <div key={v.version} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`vm-precios-version-item-${v.version}`}>
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-100 text-[11px] font-extrabold text-slate-600">V{v.version}</div>
                    <div className="min-w-0 flex-1 leading-tight">
                      <div className="text-[12px] font-bold text-slate-700 truncate">{v.label}</div>
                      <div className="text-[10px] text-slate-400 truncate">{v.createdAt ? new Date(v.createdAt).toLocaleString("es-PE") : ""}{v.createdBy ? ` · ${v.createdBy}` : ""}</div>
                    </div>
                    {v.vigente ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-extrabold text-emerald-700">VIGENTE</span>
                    ) : (
                      <button onClick={() => restoreVersion(v)} disabled={restoring} data-testid={`vm-precios-restore-${v.version}`}
                        className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[10px] font-extrabold text-white disabled:opacity-40" style={{ background: PETROL }}>
                        {restoring ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Cargar
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className="flex items-start gap-1.5 px-4 py-2.5 bg-slate-50 text-[10px] text-slate-500">
              <Info size={12} className="shrink-0 mt-0.5" />
              Restaurar no cambia la versión vigente: carga esos precios como borrador. Publícalos con ACTUALIZAR PRECIOS para generar una nueva versión.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
